import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { IGN_CADASTRE_URL } from '@/constants/tiles'
import { reverseParcelle } from '@/api/geo'
import {
  BDNB_TILES_URL,
  BDNB_SOURCE_ID,
  BDNB_SOURCE_LAYER,
  BDNB_FILL_LAYER_ID,
  BDNB_LINE_LAYER_ID,
  BDNB_MIN_ZOOM,
  IRIS_TILES_URL,
  IRIS_SOURCE_ID,
  IRIS_SOURCE_LAYER,
  IRIS_FILL_LAYER_ID,
  IRIS_LINE_LAYER_ID,
  IRIS_MIN_ZOOM,
  IRIS_MAX_ZOOM,
  buildDPEColorExpression,
  DPE_COLORS,
  DPE_CLASSES,
  DPE_COLOR_MISSING,
} from '@/constants/bdnb'

const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY

const SELECTION_FILL_ID = 'parcelle-selection-fill'
const SELECTION_LINE_ID = 'parcelle-selection-line'
const SELECTION_SOURCE_ID = 'parcelle-selection'
const CADASTRE_MIN_ZOOM = 15

export default function MapBDNB({
  latitude,
  longitude,
  zoom = 18,
  height = '500px',
  selectedParcelles = [],
  onParcelleToggle,
  onMarkerDragEnd,
}) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const onParcelleToggleRef = useRef(onParcelleToggle)
  onParcelleToggleRef.current = onParcelleToggle
  const onMarkerDragEndRef = useRef(onMarkerDragEnd)
  onMarkerDragEndRef.current = onMarkerDragEnd
  const markerDraggingRef = useRef(false)

  // Initialisation unique de la carte
  useEffect(() => {
    if (!containerRef.current || !latitude || !longitude || !MAPTILER_KEY) return
    if (mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          'maptiler-base': {
            type: 'raster',
            tiles: [`https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.maptiler.com/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/">OSM</a>',
          },
          'cadastre-ign': {
            type: 'raster',
            tiles: [IGN_CADASTRE_URL],
            tileSize: 256,
          },
          [BDNB_SOURCE_ID]: {
            type: 'vector',
            tiles: [BDNB_TILES_URL],
            minzoom: BDNB_MIN_ZOOM,
            maxzoom: BDNB_MIN_ZOOM,
          },
          [IRIS_SOURCE_ID]: {
            type: 'vector',
            tiles: [IRIS_TILES_URL],
            minzoom: IRIS_MIN_ZOOM,
            maxzoom: IRIS_MAX_ZOOM,
          },
          [SELECTION_SOURCE_ID]: {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          },
        },
        layers: [
          // 1. Fond MapTiler
          { id: 'maptiler-base', type: 'raster', source: 'maptiler-base' },
          // 2. Cadastre IGN (zoom 15+)
          {
            id: 'cadastre-raster',
            type: 'raster',
            source: 'cadastre-ign',
            minzoom: CADASTRE_MIN_ZOOM,
            paint: { 'raster-opacity': 0.5 },
          },
          // 3. IRIS (zoom 9-13)
          {
            id: IRIS_FILL_LAYER_ID,
            type: 'fill',
            source: IRIS_SOURCE_ID,
            'source-layer': IRIS_SOURCE_LAYER,
            minzoom: IRIS_MIN_ZOOM,
            maxzoom: BDNB_MIN_ZOOM,
            paint: { 'fill-color': '#627BC1', 'fill-opacity': 0.15 },
          },
          {
            id: IRIS_LINE_LAYER_ID,
            type: 'line',
            source: IRIS_SOURCE_ID,
            'source-layer': IRIS_SOURCE_LAYER,
            minzoom: IRIS_MIN_ZOOM,
            maxzoom: BDNB_MIN_ZOOM,
            paint: { 'line-color': '#627BC1', 'line-width': 1 },
          },
          // 4. Batiments DPE (zoom 14+)
          {
            id: BDNB_FILL_LAYER_ID,
            type: 'fill',
            source: BDNB_SOURCE_ID,
            'source-layer': BDNB_SOURCE_LAYER,
            minzoom: BDNB_MIN_ZOOM,
            paint: { 'fill-color': buildDPEColorExpression(), 'fill-opacity': 0.7 },
          },
          {
            id: BDNB_LINE_LAYER_ID,
            type: 'line',
            source: BDNB_SOURCE_ID,
            'source-layer': BDNB_SOURCE_LAYER,
            minzoom: BDNB_MIN_ZOOM,
            paint: { 'line-color': '#555', 'line-width': 0.5 },
          },
          // 5. Selection parcelles (GeoJSON)
          {
            id: SELECTION_FILL_ID,
            type: 'fill',
            source: SELECTION_SOURCE_ID,
            paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.25 },
          },
          {
            id: SELECTION_LINE_ID,
            type: 'line',
            source: SELECTION_SOURCE_ID,
            paint: { 'line-color': '#1d4ed8', 'line-width': 2.5 },
          },
        ],
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      },
      center: [longitude, latitude],
      zoom,
      maxPitch: 0,
    })

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')

    // Marker adresse (draggable si callback fourni)
    const isDraggable = !!onMarkerDragEndRef.current
    markerRef.current = new maplibregl.Marker({ color: '#F26522', draggable: isDraggable })
      .setLngLat([longitude, latitude])
      .addTo(map)

    if (isDraggable) {
      const el = markerRef.current.getElement()
      el.style.zIndex = '10'
      el.style.cursor = 'grab'
      el.addEventListener('mousedown', () => { el.style.cursor = 'grabbing' })
      el.addEventListener('mouseup', () => { el.style.cursor = 'grab' })
    }

    markerRef.current.on('dragstart', () => {
      markerDraggingRef.current = true
    })
    markerRef.current.on('dragend', () => {
      if (!onMarkerDragEndRef.current) return
      const lngLat = markerRef.current.getLngLat()
      onMarkerDragEndRef.current({ latitude: lngLat.lat, longitude: lngLat.lng })
      setTimeout(() => { markerDraggingRef.current = false }, 500)
    })

    // Popup DPE au clic sur un batiment (seulement si pas de selection parcelle active)
    map.on('click', BDNB_FILL_LAYER_ID, (e) => {
      if (onParcelleToggleRef.current && map.getZoom() >= CADASTRE_MIN_ZOOM) return
      const props = e.features?.[0]?.properties
      if (!props) return

      new maplibregl.Popup({ maxWidth: '320px' })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-size:13px;line-height:1.5">
            <strong>DPE : ${props.classe_bilan_dpe || 'N/A'}</strong><br/>
            Annee : ${props.annee_construction || '?'}<br/>
            Hauteur : ${props.hauteur_mean ? Number(props.hauteur_mean).toFixed(1) + ' m' : '?'}<br/>
            Logements : ${props.nb_log || '?'}<br/>
            Usage : ${props.usage_niveau_1_txt || '?'}
          </div>
        `)
        .addTo(map)
    })

    // Clic parcelle — selection/deselection
    map.on('click', async (e) => {
      if (markerDraggingRef.current) return
      if (!onParcelleToggleRef.current) return
      if (map.getZoom() < CADASTRE_MIN_ZOOM) return

      // Indicateur de chargement
      const el = document.createElement('div')
      Object.assign(el.style, {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: 'rgba(59,130,246,0.6)',
        boxShadow: '0 0 0 0 rgba(59,130,246,0.4)',
        animation: 'parcelle-ping 1s ease-in-out infinite',
      })
      const loading = new maplibregl.Marker({ element: el }).setLngLat(e.lngLat).addTo(map)

      try {
        const feature = await reverseParcelle(e.lngLat.lat, e.lngLat.lng)
        if (feature?.properties) {
          onParcelleToggleRef.current({
            code_insee: feature.properties.code_insee,
            prefixe: feature.properties.prefixe,
            section: feature.properties.section,
            numero: feature.properties.numero,
            contenance: feature.properties.contenance,
            geometry: feature.geometry,
          })
        }
      } catch {
        // Silencieux
      } finally {
        loading.remove()
      }
    })

    // Curseur
    map.on('mousemove', (e) => {
      if (!onParcelleToggleRef.current) return
      if (!map.getLayer(BDNB_FILL_LAYER_ID)) {
        map.getCanvas().style.cursor = map.getZoom() >= CADASTRE_MIN_ZOOM ? 'crosshair' : ''
        return
      }
      const bdnb = map.queryRenderedFeatures(e.point, { layers: [BDNB_FILL_LAYER_ID] })
      if (bdnb.length > 0) {
        map.getCanvas().style.cursor = 'pointer'
      } else if (map.getZoom() >= CADASTRE_MIN_ZOOM) {
        map.getCanvas().style.cursor = 'crosshair'
      } else {
        map.getCanvas().style.cursor = ''
      }
    })

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Init une seule fois — le repositionnement est gere dans l'effet suivant

  // Repositionnement carte + marker quand les coordonnees changent (ex: selection adresse)
  useEffect(() => {
    if (!mapRef.current || !latitude || !longitude) return
    // Skip si le changement vient du drag (carte et marker deja en place)
    if (markerDraggingRef.current) return
    mapRef.current.flyTo({ center: [longitude, latitude], zoom })
    if (markerRef.current) markerRef.current.setLngLat([longitude, latitude])
  }, [latitude, longitude, zoom])

  // Sync GeoJSON selection depuis les parcelles du store
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const fc = {
      type: 'FeatureCollection',
      features: selectedParcelles
        .filter((p) => p._geometry)
        .map((p) => ({
          type: 'Feature',
          geometry: p._geometry,
          properties: {},
        })),
    }

    const update = () => {
      const source = map.getSource(SELECTION_SOURCE_ID)
      if (source) source.setData(fc)
    }

    if (map.isStyleLoaded()) {
      update()
    } else {
      const onLoad = () => update()
      map.on('load', onLoad)
      return () => map.off('load', onLoad)
    }
  }, [selectedParcelles])

  if (!latitude || !longitude) return null

  return (
    <div>
      <style>{`
        @keyframes parcelle-ping {
          0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.6); }
          70% { box-shadow: 0 0 0 12px rgba(59,130,246,0); }
          100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
        }
      `}</style>
      <div ref={containerRef} style={{ height, width: '100%' }} className="rounded-lg" />
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <span className="text-xs text-slate-500 font-medium">DPE :</span>
        {DPE_CLASSES.map((cls) => (
          <span key={cls} className="flex items-center gap-1 text-xs">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ backgroundColor: DPE_COLORS[cls] }}
            />
            {cls}
          </span>
        ))}
        <span className="flex items-center gap-1 text-xs">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: DPE_COLOR_MISSING }}
          />
          N/A
        </span>
        {onParcelleToggle && (
          <span className="text-xs text-slate-400 ml-auto">
            Cliquez sur une parcelle pour la selectionner{onMarkerDragEnd ? ' — deplacez le marqueur pour changer l\'adresse' : ''}
          </span>
        )}
      </div>
    </div>
  )
}
